import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Check, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { ManagedCampaignData, initialManagedCampaignData } from "./ManagedCampaignData";
import { CampaignBasicsStep } from "./CampaignBasicsStep";
import { ManagedScheduleStep } from "./ManagedScheduleStep";
import { ManagedGeographyStep } from "./ManagedGeographyStep";
import { ManagedIndustryStep } from "./ManagedIndustryStep";
import { ManagedPeopleStep } from "./ManagedPeopleStep";
import { ManagedSequenceStep } from "./ManagedSequenceStep";
import { ManagedReviewStep } from "./ManagedReviewStep";
import { formatCapacityError, isCapacityError } from "@/hooks/useAccountCapacity";
import { buildSequenceRows } from "@/lib/sequenceRows";


// localStorage key for persisting wizard state (scoped by workspace)
const STORAGE_KEY = 'managed_campaign_wizard_progress';

const STEPS = [
  { id: 1, name: "Basics", description: "Campaign name & account" },
  { id: 2, name: "Schedule", description: "Timing & limits" },
  { id: 3, name: "Geography", description: "Target locations" },
  { id: 4, name: "Industry", description: "Companies to target" },
  { id: 5, name: "People", description: "Who to reach" },
  { id: 6, name: "Sequence", description: "Your messages" },
  { id: 7, name: "Review", description: "Confirm & submit" },
];

export const ManagedCampaignWizard = ({ guestToken }: { guestToken?: string }) => {
  const navigate = useNavigate();
  const { workspace, isWorkspaceAdminOrOwner } = useWorkspace();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientName, setClientName] = useState<string>("");
  const [isTestMode, setIsTestMode] = useState(false);
  const [hasSavedProgress, setHasSavedProgress] = useState(false);

  // Initialize state from localStorage if available (scoped by workspace)
  const [currentStep, setCurrentStep] = useState(() => {
    if (typeof window !== 'undefined' && workspace?.id) {
      const saved = localStorage.getItem(`${STORAGE_KEY}_${workspace.id}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return parsed.currentStep || 1;
        } catch {
          return 1;
        }
      }
    }
    return 1;
  });

  const [data, setData] = useState<ManagedCampaignData>(() => {
    if (typeof window !== 'undefined' && workspace?.id) {
      const saved = localStorage.getItem(`${STORAGE_KEY}_${workspace.id}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return parsed.data || initialManagedCampaignData;
        } catch {
          return initialManagedCampaignData;
        }
      }
    }
    return initialManagedCampaignData;
  });

  // Check if there's saved progress on mount
  useEffect(() => {
    if (workspace?.id) {
      const saved = localStorage.getItem(`${STORAGE_KEY}_${workspace.id}`);
      setHasSavedProgress(!!saved);
    }
  }, [workspace?.id]);

  // Load saved state when workspace becomes available
  useEffect(() => {
    if (workspace?.id) {
      const saved = localStorage.getItem(`${STORAGE_KEY}_${workspace.id}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.data) setData(parsed.data);
          if (parsed.currentStep) setCurrentStep(parsed.currentStep);
        } catch (e) {
          console.error('Failed to parse saved wizard progress:', e);
        }
      }
    }
  }, [workspace?.id]);

  // Save state to localStorage whenever data or currentStep changes
  useEffect(() => {
    if (workspace?.id) {
      const stateToSave = {
        data,
        currentStep,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(`${STORAGE_KEY}_${workspace.id}`, JSON.stringify(stateToSave));

      // Show reset button if there's any progress
      const hasProgress = currentStep > 1 ||
        data.campaignName.trim().length > 0 ||
        data.countries.length > 0 ||
        data.industries.length > 0 ||
        data.departments.length > 0;
      setHasSavedProgress(hasProgress);
    }
  }, [data, currentStep, workspace?.id]);

  // Clear saved state
  const clearSavedProgress = () => {
    if (workspace?.id) {
      localStorage.removeItem(`${STORAGE_KEY}_${workspace.id}`);
      setHasSavedProgress(false);
    }
  };

  // Reset form to initial state
  const handleReset = () => {
    clearSavedProgress();
    setData(initialManagedCampaignData);
    setCurrentStep(1);
    toast.success("Form reset successfully");
  };

  // Fetch client name when clientId changes
  useEffect(() => {
    const fetchClientName = async () => {
      if (!data.clientId) return;
      const { data: client } = await supabase
        .from("clients")
        .select("linkedin_name, email")
        .eq("id", data.clientId)
        .single();
      if (client) {
        setClientName(client.linkedin_name || client.email);
      }
    };
    fetchClientName();
  }, [data.clientId]);

  const updateData = (updates: Partial<ManagedCampaignData>) => {
    setData(prev => ({ ...prev, ...updates }));
  };

  const handleTestMode = () => {
    if (!isWorkspaceAdminOrOwner) return;

    const dummyData: Partial<ManagedCampaignData> = {
      countries: ["United States", "United Kingdom"],
      cities: ["San Francisco", "New York", "London"],
      industries: ["Software Development", "Information Technology", "SaaS"],
      companySizes: ["11-50", "51-200", "201-500"],
      companyKeywords: ["AI", "Machine Learning", "B2B", "Enterprise"],
      departments: ["Engineering", "Product", "Sales"],
      jobTitles: ["CTO", "VP of Engineering", "Product Manager", "Founder"],
      seniorityLevels: ["CXO", "VP", "Director", "Owner"],
      additionalNotes: "This is a test campaign generated for validation purposes. Please proceed with standard setup protocols.",
      sequences: [
        {
          step_number: 1,
          action_type: "connect",
          delay_hours: 0,
          include_message: true,
          message_template: "Hi {first_name}, I noticed we operate in similar spaces regarding SaaS technology. Would love to connect and share insights."
        },
        {
          step_number: 2,
          action_type: "message",
          delay_hours: 24,
          include_message: true,
          message_template: "Thanks for accepting! I'm curious how {company} handles scalable outreach currently? We've been working on some interesting tools."
        },
        {
          step_number: 3,
          action_type: "end",
          delay_hours: 0
        }
      ]
    };

    setData(prev => ({ ...prev, ...dummyData }));
    setIsTestMode(true);
  };


  const isStepComplete = (stepId: number): boolean => {
    switch (stepId) {
      case 1:
        return data.campaignName.trim().length > 0;
      case 2:
        return data.workingDays.length > 0 && data.workingHoursEnd > data.workingHoursStart;
      case 3:
        return data.countries.length > 0;
      case 4:
        return data.industries.length > 0;
      case 5:
        return data.departments.length > 0;
      case 6:
        const activeSteps = data.sequences.filter(s => s.action_type !== "end");
        return activeSteps.length > 0;
      case 7:
        return true;
      default:
        return false;
    }
  };

  const currentStepCanProceed = isStepComplete(currentStep);

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    } else {
      // Clear persisted state when going back to campaigns list
      clearSavedProgress();
      navigate("/campaigns");
    }
  };

  const handleSubmit = async () => {
    if (!workspace?.id || (!guestToken && !user?.id)) {
      toast.error("Session error. Please refresh and try again.");
      return;
    }

    if (!data.clientId) {
      toast.error("Please select a LinkedIn account for the campaign.");
      return;
    }

    setIsSubmitting(true);

    try {
      if (guestToken) {
        // Guest Submission via RPC
        const { error } = await supabase.rpc('submit_guest_campaign', {
          token_uuid: guestToken,
          campaign_data: JSON.parse(JSON.stringify({
            ...data,
            sequences: data.sequences
          }))
        });

        if (error) throw error;
      } else {
        if (!user?.id) throw new Error("User ID is missing"); // Should be caught by earlier check but for TS

        // Authenticated Submission (Existing Logic)
        // Step 1: Create draft campaign
        const { data: campaign, error: campaignError } = await supabase
          .from("campaigns")
          .insert([{
            user_id: user.id,
            client_id: data.clientId,
            name: data.campaignName,
            status: "draft",
            timezone: data.timezone,
            working_hours_start: data.workingHoursStart,
            working_hours_end: data.workingHoursEnd,
            working_days: data.workingDays,
            max_connections_daily: data.maxConnectionsDaily,
            max_messages_daily: data.maxMessagesDaily,
            respect_working_hours: !(data.workingHoursStart === 0 && data.workingHoursEnd === 24),
          }])
          .select("id")
          .single();

        if (campaignError) throw campaignError;

        // Step 2: Create sequences for the campaign
        const sequencesToInsert = buildSequenceRows(data.sequences, campaign.id, {
          normalizeVariables: false,
        });

        const { error: sequenceError } = await supabase
          .from("sequences")
          .insert(sequencesToInsert);

        if (sequenceError) throw sequenceError;

        // Step 3: Create managed campaign request linked to the draft
        const { error: requestError } = await supabase.from("managed_campaign_requests").insert([{
          workspace_id: workspace.id,
          user_id: user.id,
          campaign_name: data.campaignName,
          client_id: data.clientId,
          campaign_id: campaign.id, // Link to draft campaign
          target_countries: data.countries,
          target_cities: data.cities,
          target_industries: data.industries,
          company_sizes: data.companySizes,
          company_keywords: data.companyKeywords,
          person_keywords: data.personKeywords,
          target_departments: data.departments,
          job_titles: data.jobTitles,
          seniority_levels: data.seniorityLevels,
          additional_notes: data.additionalNotes,
          sequence_template: JSON.parse(JSON.stringify(data.sequences)),
          timezone: data.timezone,
          working_hours_start: data.workingHoursStart,
          working_hours_end: data.workingHoursEnd,
          working_days: data.workingDays,
          max_connections_daily: data.maxConnectionsDaily,
          max_messages_daily: data.maxMessagesDaily,
          status: "pending_setup",
        }]);

        if (requestError) throw requestError;

        // Send email notification to super admins (fire and forget)
        try {
          await supabase.functions.invoke('send-email', {
            body: {
              action: 'notify_super_admins',
              templateSlug: 'managed_campaign_request_notification',
              variables: {
                request_id: campaign.id,
                workspace_name: workspace.name || 'Unknown Workspace',
                campaign_name: data.campaignName,
                client_name: clientName || 'Not assigned',
                target_countries: data.countries.join(', '),
                target_industries: data.industries.join(', '),
                target_departments: data.departments.join(', '),
                job_titles: data.jobTitles.join(', '),
                max_connections_daily: data.maxConnectionsDaily,
                max_messages_daily: data.maxMessagesDaily,
                additional_notes: data.additionalNotes || '',
                admin_url: `${window.location.origin}/admin/managed-campaigns`,
              }
            }
          });
        } catch (emailError) {
          console.error('Failed to send admin notification email:', emailError);
        }
      }

      // Clear persisted state after successful submission
      clearSavedProgress();
      toast.success("Campaign request submitted! Our team will add targets and launch within 24 hours.");
      navigate("/campaigns");
    } catch (error: any) {
      console.error("Error submitting campaign request:", error);
      toast.error(
        isCapacityError(error)
          ? formatCapacityError(error)
          : error.message || "Failed to submit campaign request"
      );

    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <CampaignBasicsStep data={data} updateData={updateData} onTestMode={handleTestMode} />;
      case 2:
        return <ManagedScheduleStep data={data} updateData={updateData} />;
      case 3:
        return <ManagedGeographyStep data={data} updateData={updateData} />;
      case 4:
        return <ManagedIndustryStep data={data} updateData={updateData} />;
      case 5:
        return <ManagedPeopleStep data={data} updateData={updateData} />;
      case 6:
        return <ManagedSequenceStep data={data} updateData={updateData} />;
      case 7:
        return <ManagedReviewStep data={data} clientName={clientName} />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full hover:bg-muted"
          onClick={handleBack}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">Create Automatic Campaign</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tell us your target audience and we'll handle the rest
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2 rounded-full border-primary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary/30 text-primary transition-all"
            onClick={() => navigate("/campaigns/managed/how-it-works")}
          >
            <ArrowRight className="w-4 h-4 rotate-[-45deg]" />
            <span className="hidden sm:inline">How it works</span>
          </Button>
          {hasSavedProgress && (
            <Button variant="outline" className="gap-2" onClick={handleReset}>
              <RotateCcw className="w-4 h-4" />
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Progress Steps */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex items-center justify-between min-w-max">
          {STEPS.map((step, index) => {
            const isComplete = isStepComplete(step.id) && (step.id < currentStep || isTestMode);
            const isActive = currentStep === step.id;
            const isClickable = index === 0 || isStepComplete(STEPS[index - 1].id);

            return (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => isClickable && setCurrentStep(step.id)}
                  disabled={!isClickable}
                  className="flex flex-col items-center group focus:outline-none"
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${isActive
                      ? "bg-primary text-primary-foreground ring-4 ring-primary/20"
                      : isComplete
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                      } ${isClickable ? "cursor-pointer group-hover:scale-105" : "cursor-not-allowed opacity-70"}`}
                  >
                    {isComplete && !isActive ? <Check className="w-4 h-4" /> : step.id}
                  </div>
                  <span className={`text-xs mt-1 hidden md:block transition-colors ${isActive ? "text-primary font-medium" : isComplete ? "text-primary" : "text-muted-foreground"
                    }`}>
                    {step.name}
                  </span>
                </button>
                {index < STEPS.length - 1 && (
                  <div
                    className={`w-6 md:w-12 h-0.5 mx-1 transition-colors ${isComplete ? "bg-primary" : "bg-muted"
                      }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <Card className="p-6 md:p-8 min-h-[400px]">
        <div className="mb-6">
          <h2 className="text-lg font-semibold">{STEPS[currentStep - 1].name}</h2>
          <p className="text-sm text-muted-foreground">{STEPS[currentStep - 1].description}</p>
        </div>
        {renderStep()}
      </Card>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {currentStep < STEPS.length ? (
          <Button onClick={handleNext} disabled={!isStepComplete(currentStep)}>
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !isStepComplete(currentStep)}
            className="min-w-[140px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Submit Request
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
